/**
 * PARTNER-ACC-1 · 同行详情「基础信息」标签里的「同行站登录账号」卡。
 * 契约：designs/mockups/channel-partners/同行登录账号-样稿.html（tag partner-acc-mockup-v1）。
 * 同行用这些账号登录同行站；每家同行最多 1 主 + 1 副；停用的账号不占名额；邀请 7 天有效；后台看不到、也设不了对方的密码。
 * 谁能看 / 操作：账号管理权限点 channel_partners.accounts.manage（外层用 <Can> 包，没有权限整张卡不渲染）。
 * 接口：GET/POST /channel-partners/{id}/accounts · POST …/{aid}/resend-invite · POST …/{aid}/reset-password ·
 *       PATCH …/{aid} {status} · POST …/{aid}:unlock（B 端已有 + 本单新增解锁）。
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { partnerAccountsApi, type PartnerAccount } from "@/services/b2b";

const fmtDay = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
};
const fmtTime = (iso: string) => {
  const d = new Date(iso);
  return `${fmtDay(iso)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

type Tone = "amber" | "green" | "gray" | "red";
const TONE: Record<Tone, string> = {
  amber: "bg-amber-50 text-amber-600",
  green: "bg-emerald-50 text-emerald-600",
  gray: "bg-slate-100 text-slate-600",
  red: "bg-rose-50 text-rose-600",
};
function StatePill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium ${TONE[tone]}`}>
      <span className="h-[7px] w-[7px] rounded-full bg-current" />
      {children}
    </span>
  );
}

/** 4 种状态：已邀请 · 待设密码 / 正常 / 已停用 / 已锁定（正常账号被锁） */
export type AccountState = "invited" | "active" | "disabled" | "locked";
export function accountState(a: PartnerAccount): AccountState {
  if (a.status === "disabled") return "disabled";
  if (a.status === "invited") return "invited";
  return a.locked ? "locked" : "active";
}

/** OPENAPI-2 · developer = 开放平台「技术对接」账号（只能登录开放平台）；停用 / 重置密码 / 重发邀请与主副账号同样出现 */
const ROLE_LABEL: Record<PartnerAccount["role"], string> = { primary: "主账号", secondary: "副账号", developer: "技术对接" };

const btn = "rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50";

type Confirm = { kind: "disable" | "reset"; account: PartnerAccount } | null;

export function PartnerAccountsCard({ partnerId }: { partnerId: string }) {
  const qc = useQueryClient();
  const key = ["b2b", "partner-accounts", partnerId];
  const q = useQuery({ queryKey: key, queryFn: () => partnerAccountsApi.list(partnerId) });
  const rows = q.data ?? [];
  const [showNew, setShowNew] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [flash, setFlash] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  const done = (text: string) => { setFlash({ tone: "ok", text }); qc.invalidateQueries({ queryKey: key }); };
  const fail = (e: unknown) => setFlash({ tone: "err", text: (e as Error).message || "操作失败" });

  const resend = useMutation({ mutationFn: (a: PartnerAccount) => partnerAccountsApi.resendInvite(partnerId, a.id), onSuccess: (_r, a) => done(`已重发邀请邮件给 ${a.email}`), onError: fail });
  const reset = useMutation({ mutationFn: (a: PartnerAccount) => partnerAccountsApi.resetPassword(partnerId, a.id), onSuccess: (_r, a) => { setConfirm(null); done(`已给 ${a.email} 发了设新密码的邮件，旧密码已作废`); }, onError: (e) => { setConfirm(null); fail(e); } });
  const setStatus = useMutation({
    mutationFn: (v: { a: PartnerAccount; status: "active" | "disabled" }) => partnerAccountsApi.setStatus(partnerId, v.a.id, v.status),
    onSuccess: (_r, v) => { setConfirm(null); done(v.status === "disabled" ? `已停用 ${v.a.email}（登录态已作废，名额已释放）` : `已启用 ${v.a.email}`); },
    onError: (e) => { setConfirm(null); fail(e); },
  });
  const unlock = useMutation({ mutationFn: (a: PartnerAccount) => partnerAccountsApi.unlock(partnerId, a.id), onSuccess: (_r, a) => done(`已解锁 ${a.email}`), onError: fail });

  const live = rows.filter((a) => a.status !== "disabled");
  const livePrimary = live.find((a) => a.role === "primary");
  // 1 主 + 1 副的名额只算主 / 副账号；开放平台的「技术对接」账号（developer）不占名额
  const full = live.filter((a) => a.role !== "developer").length >= 2;
  const busy = resend.isPending || reset.isPending || setStatus.isPending || unlock.isPending;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="partner-accounts-card">
      <div className="mb-1 flex items-center gap-2 text-[14px] font-semibold text-slate-900">
        同行站登录账号
        <span className="rounded border border-orange-300 bg-orange-50 px-1 text-[10.5px] font-bold text-orange-800">新</span>
        <button
          type="button" data-testid="pa-new" disabled={full}
          title={full ? "已满 1 主 1 副，请先停用一个" : undefined}
          onClick={() => { setFlash(null); setShowNew(true); }}
          className="ml-auto rounded-lg bg-blue-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
        >＋ 新建账号</button>
      </div>
      <div className="mb-3 text-[12px] text-slate-400">
        同行用这些账号登录同行站（<span className="rounded bg-slate-100 px-1.5 font-mono text-[11.5px]">{import.meta.env.VITE_PARTNER_SITE_URL || `${window.location.origin}/partner`}</span>   {/* PARTDOM-1 · 公司站构建时带 VITE_PARTNER_SITE_URL=https://partner.utour.ai；个人站不设 = 原样 */}）查价、下单、看对账。
        每家同行最多 <b className="text-slate-600">1 个主账号 + 1 个副账号</b>；新建后系统给对方发邀请邮件，对方点链接自己设密码，链接 <b className="text-slate-600">7 天</b>有效。
      </div>

      {flash && (
        <div role="status" data-testid="pa-flash" className={`mb-3 rounded-lg border px-3 py-2 text-[12.5px] ${flash.tone === "ok" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
          {flash.text}
        </div>
      )}

      {q.isLoading ? (
        <div className="py-6 text-center text-[13px] text-slate-400">加载中…</div>
      ) : q.isError ? (
        <button type="button" onClick={() => q.refetch()} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">加载失败，点击重试</button>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 py-6 text-center text-[13px] text-slate-400">还没有登录账号，点右上角「＋ 新建账号」给对方发邀请</div>
      ) : (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-slate-200 text-left text-[12px] font-medium text-slate-400">
              <th className="py-2">姓名</th><th>邮箱</th><th>电话</th><th>身份</th><th>状态</th><th>最近登录</th><th className="text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const st = accountState(a);
              const expires = a.invite_expires_at ? new Date(a.invite_expires_at) : null;
              const sentAt = expires ? new Date(expires.getTime() - 7 * 86400_000).toISOString() : null;
              const expired = expires != null && expires.getTime() < Date.now();
              return (
                <tr key={a.id} className="border-b border-slate-100" data-testid={`pa-row-${a.email}`} data-state={st}>
                  <td className="py-2.5 font-semibold text-slate-900">{a.full_name}</td>
                  <td><span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[12px]">{a.email}</span></td>
                  <td>{a.phone || "—"}</td>
                  <td>{ROLE_LABEL[a.role] ?? a.role}</td>
                  <td>
                    {st === "invited" && <StatePill tone="amber">已邀请 · 待设密码</StatePill>}
                    {st === "active" && <StatePill tone="green">正常</StatePill>}
                    {st === "disabled" && <StatePill tone="gray">已停用</StatePill>}
                    {st === "locked" && <StatePill tone="red">已锁定 · 登错太多次</StatePill>}
                    {st === "invited" && expires && sentAt && (
                      <div className={`mt-0.5 text-[11.5px] ${expired ? "text-rose-500" : "text-slate-400"}`}>
                        {expired ? `邀请已过期（${fmtDay(expires.toISOString())}），请重发邀请` : `邀请 ${fmtDay(sentAt)} 发出 · ${fmtDay(expires.toISOString())} 过期`}
                      </div>
                    )}
                    {st === "locked" && a.locked_until && <div className="mt-0.5 text-[11.5px] text-slate-400">{fmtTime(a.locked_until)} 自动解锁，也可手动解锁</div>}
                  </td>
                  <td className="tabular-nums">{a.last_login_at ? fmtTime(a.last_login_at) : "—"}</td>
                  <td className="space-x-1.5 text-right">
                    {st === "invited" && <button type="button" disabled={busy} className={btn} onClick={() => resend.mutate(a)}>重发邀请</button>}
                    {st === "locked" && <button type="button" disabled={busy} className={btn} onClick={() => unlock.mutate(a)}>解锁</button>}
                    {(st === "active" || st === "locked") && <button type="button" disabled={busy} className={btn} onClick={() => setConfirm({ kind: "reset", account: a })}>重置密码</button>}
                    {st !== "disabled" && <button type="button" disabled={busy} className={btn} onClick={() => setConfirm({ kind: "disable", account: a })}>停用</button>}
                    {st === "disabled" && <button type="button" disabled={busy} className={btn} onClick={() => setStatus.mutate({ a, status: "active" })}>启用</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="mt-2.5 text-[11.5px] text-slate-400">
        其它状态：<b className="font-medium text-slate-500">已停用</b>（按钮变「启用」）· <b className="font-medium text-slate-500">已锁定</b>（登错太多次，按钮多一个「解锁」）。
        <b className="text-slate-500">停用的账号不占名额</b>，可以再建一个新的（已停用账号的邮箱不能再新建，要用走「启用」）。
        重置密码 = 给对方发一封设新密码的邮件，后台看不到也设不了对方的密码。
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 p-4" onClick={() => setConfirm(null)}>
          <div role="alertdialog" data-testid="pa-confirm" className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1.5 text-[15px] font-bold text-slate-900">{confirm.kind === "disable" ? "停用这个账号？" : "重置这个账号的密码？"}</div>
            <div className="mb-4 text-[12.5px] text-slate-600">
              <b>{confirm.account.full_name}</b>（{confirm.account.email}）
              <div className="mt-1.5">
                {confirm.kind === "disable"
                  ? "停用后对方马上不能登录，已登录的设备也会被踢下线；名额释放，可以再建一个同身份的新账号。之后随时可以「启用」。"
                  : "旧密码立刻作废，系统给对方发一封设新密码的邮件（链接 7 天有效）。后台看不到、也设不了对方的密码。"}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirm(null)} className={btn}>取消</button>
              <button
                type="button" disabled={busy} data-testid="pa-confirm-ok"
                onClick={() => (confirm.kind === "disable" ? setStatus.mutate({ a: confirm.account, status: "disabled" }) : reset.mutate(confirm.account))}
                className="rounded-lg bg-rose-600 px-3 py-1 text-[12.5px] font-semibold text-white disabled:opacity-50"
              >{confirm.kind === "disable" ? "确认停用" : "确认重置"}</button>
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <NewAccountModal
          partnerId={partnerId} hasLivePrimary={!!livePrimary} hasLiveSecondary={live.some((a) => a.role === "secondary")}
          primaryEmail={livePrimary?.email}
          onClose={() => setShowNew(false)}
          onCreated={(email) => { setShowNew(false); done(`已创建账号并给 ${email} 发了邀请邮件`); }}
        />
      )}
    </section>
  );
}

function NewAccountModal({
  partnerId, hasLivePrimary, hasLiveSecondary, primaryEmail, onClose, onCreated,
}: { partnerId: string; hasLivePrimary: boolean; hasLiveSecondary: boolean; primaryEmail?: string; onClose: () => void; onCreated: (email: string) => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<"primary" | "secondary">(hasLivePrimary ? "secondary" : "primary");
  const [err, setErr] = useState<string | null>(null);
  const roleTaken = role === "primary" ? hasLivePrimary : hasLiveSecondary;
  const valid = name.trim() !== "" && /\S+@\S+\.\S+/.test(email.trim()) && !roleTaken;
  const create = useMutation({
    mutationFn: () => partnerAccountsApi.create(partnerId, { full_name: name.trim(), email: email.trim(), phone: phone.trim() || undefined, role }),
    onSuccess: (a) => onCreated(a.email),
    onError: (e) => setErr((e as Error).message || "创建失败"),
  });
  const field = "h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]";
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/35 p-4 pt-[12vh]" onClick={onClose}>
      <div role="dialog" data-testid="pa-new-modal" className="flex w-full max-w-[440px] flex-col gap-3 rounded-[14px] bg-white p-[18px] shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center text-[16px] font-semibold text-slate-900">新建登录账号<button type="button" aria-label="关闭" onClick={onClose} className="ml-auto text-[18px] text-slate-400">✕</button></div>
        <label className="text-[12.5px] text-slate-600">姓名 *<input className={`${field} mt-1`} value={name} onChange={(e) => setName(e.target.value)} aria-label="姓名" /></label>
        <label className="text-[12.5px] text-slate-600">邮箱 *（邀请邮件发到这里，也是登录名）<input className={`${field} mt-1`} value={email} onChange={(e) => setEmail(e.target.value)} aria-label="邮箱" /></label>
        <label className="text-[12.5px] text-slate-600">电话<input className={`${field} mt-1`} placeholder="选填" value={phone} onChange={(e) => setPhone(e.target.value)} aria-label="电话" /></label>
        <div className="text-[12.5px] text-slate-600">
          身份 *
          <div className="mt-1 inline-flex overflow-hidden rounded-lg border border-slate-200" role="radiogroup" aria-label="身份">
            {([["primary", "主账号"], ["secondary", "副账号"]] as const).map(([v, label]) => {
              const disabled = v === "primary" ? hasLivePrimary : hasLiveSecondary;
              return (
                <button
                  key={v} type="button" role="radio" aria-checked={role === v} disabled={disabled}
                  title={disabled ? `已有生效的${label}${v === "primary" && primaryEmail ? ` ${primaryEmail}` : ""}` : undefined}
                  onClick={() => setRole(v)}
                  className={`px-4 py-1.5 text-[13px] ${role === v ? "bg-blue-600 font-semibold text-white" : "bg-white text-slate-600"} ${disabled ? "cursor-not-allowed opacity-40" : ""}`}
                >{label}</button>
              );
            })}
          </div>
          <div className="mt-1 text-[11.5px] text-slate-400">已有主账号时「主账号」不可选</div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">
          点「创建并发邀请」后，系统马上给这个邮箱发邀请邮件（发件人是系统配置的公司邮箱）。
        </div>
        {err && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btn}>取消</button>
          <button type="button" disabled={!valid || create.isPending} onClick={() => { setErr(null); create.mutate(); }} className="rounded-lg bg-blue-600 px-4 py-1.5 text-[13px] font-semibold text-white disabled:opacity-50">
            {create.isPending ? "创建中…" : "创建并发邀请"}
          </button>
        </div>
      </div>
    </div>
  );
}
